import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { OTPInput as BaseOTPInput } from "input-otp";
import { colors, fontSize } from "style";

/**
 * OTPInput
 *
 * @param {string}    name - The name of the OTP input field.
 * @param {string}    value - The current value of the OTP input.
 * @param {function}  onChange - Callback function to handle changes in the OTP input value.
 * @param {number}    maxLength - The maximum length of the OTP input.
 */
const OTPInput = ({ maxLength = 6, name, value, onChange, ...props }) => (
  <BaseOTPInput
    maxLength={maxLength}
    value={value}
    onChange={(value) => onChange(name, value)}
    render={({ slots }) => {
      const half = Math.ceil(maxLength / 2);

      return (
        <div css={styles.container}>
          {slots.slice(0, half).map((slot, index) => (
            <Slot key={index} {...slot} />
          ))}

          <div css={styles.separator} />

          {slots.slice(half).map((slot, index) => (
            <Slot key={index + half} {...slot} />
          ))}
        </div>
      );
    }}
    {...props}
  />
);

/**
 * Slot
 *
 * @param {string}  char - The character to display in the slot.
 * @param {boolean} isActive - Indicates if the slot is currently active.
 * @param {boolean} hasFakeCaret - Indicates if the slot should display a fake caret.
 */
const Slot = ({ char, isActive, hasFakeCaret }) => (
  <div css={styles.input(isActive)}>
    {char}
    {hasFakeCaret && <div css={styles.caret} />}
  </div>
);

const styles = {
  container: css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
  `,
  input: (isActive) => css`
    width: 4rem;
    height: 4rem;

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: ${fontSize.xsmall};
    font-weight: normal;

    border-radius: 0.6rem;
    border: 1px solid ${isActive ? colors.purpleRainBase : colors.grayAnatomyLight3};

    background: #fff;
    color: ${colors.secondary};

    transition: all 0.3s ease;

    ${isActive &&
    `
      box-shadow: 0px 0px 0px 3px ${colors.purpleRainLight3};
    `}

    &:hover {
      border-color: ${colors.purpleRainBase};
    }
  `,
  caret: css`
    width: 1px;
    height: 1.8rem;
    background: ${colors.secondary};
    animation: blink 1s infinite;

    @keyframes blink {
      50% {
        opacity: 0;
      }
    }
  `,
  separator: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.2rem;

    &::after {
      content: "";
      width: 0.8rem;
      height: 0.2rem;
      border-radius: 999px;
      background: ${colors.grayAnatomyLight3};
    }
  `,
};

OTPInput.propTypes = {
  maxLength: PropTypes.number,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

Slot.propTypes = {
  char: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  hasFakeCaret: PropTypes.bool.isRequired,
};

export default OTPInput;
